/**
 * Copyright (C) Siemens AG 1996-2025 ALL RIGHTS RESERVED.
 */

import {Component, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ReactiveFormsModule} from '@angular/forms';
import {BaseFormFieldDirective} from '../../base-form-field.directive';
import {IxModule} from '@siemens/ix-angular';
import {TranslateModule} from '@ngx-translate/core';
import {FieldLabelComponent} from '../field-label/field-label.component';
import {takeUntil} from 'rxjs';
import {DateTime} from 'luxon';
import {formatDateTimeValue} from '../../../../../../shared/utils/date-time-format';

@Component({
  selector: 'app-date-input',
  imports: [
    CommonModule,
    IxModule,
    TranslateModule,
    ReactiveFormsModule,
    FieldLabelComponent,
  ],
  templateUrl: './date-input.component.html',
  styleUrl: '../input-components.scss',
})
export class DateInputComponent extends BaseFormFieldDirective {
  readonly currentLocale = signal(
    this.translate.currentLang ?? this.translate.defaultLang,
  );

  constructor() {
    super();
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(({lang}) => this.currentLocale.set(lang));
  }

  /**
   * Text for a read-only date-time value (e.g. a system-managed timestamp), or
   * `null` when the date picker should be used instead. Formatted in the
   * browser's local zone; without a usable format the stored text is shown
   * as-is, because the picker would drop the time.
   */
  get readOnlyDisplayValue(): string | null {
    const raw: unknown = this.control?.value;
    if (!this.attribute?.isDisabled) {
      return null;
    }
    let value: unknown = raw;
    if (raw instanceof Date) {
      value = raw.toISOString();
    } else if (typeof raw === 'number') {
      value = DateTime.fromMillis(raw).toISO();
    }
    if (typeof value !== 'string') {
      return null;
    }
    const formatted = formatDateTimeValue(
      value,
      this.attribute.format,
      this.attribute.timeZone,
      this.currentLocale(),
    );
    if (formatted !== value) {
      return formatted;
    }
    return value.includes('T') ? value : null;
  }
}
